import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { signIn, signUp } from "~/lib/auth-client";
import { clearSessionCache } from "~/lib/auth-guard";
import { localizeInternalHref } from "~/lib/locale-navigation";
import { client } from "~/lib/orpc-client";
import { CONSENT_VERSIONS, getConsentText } from "~/lib/consent-versions";
import type { ConsentState } from "~/components/auth/ConsentCheckboxes";
import { ConsentCheckboxes } from "~/components/auth/ConsentCheckboxes";
import { AlchemaLogo } from "~/components/AlchemaLogo";
import { asBlogPageType } from "~/lib/analytics-events";
import { trackEvent } from "~/lib/posthog-client";
import { getLocale } from "~/paraglide/runtime";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/auth/signup")({
  component: SignupPage,
});

/* Eye / eye-off icons — matched to lucide stroke weight */
function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function SignupPage() {
  const navigate = useNavigate();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [referralCode, setReferralCode] = React.useState<string | null>(null);
  const [consents, setConsents] = React.useState<ConsentState>({
    terms_privacy: false,
    ai_processing: false,
    marketing_emails: false,
    product_improvement: false,
  });
  const [consentErrors, setConsentErrors] = React.useState<Partial<Record<keyof ConsentState, boolean>>>({});

  // Read referral code from sessionStorage (set by /register?ref=...)
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = sessionStorage.getItem("alchema_referral_code");
      if (stored) setReferralCode(stored);
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  function validateConsents(): boolean {
    const newErrors: Partial<Record<keyof ConsentState, boolean>> = {};
    if (!consents.terms_privacy) newErrors.terms_privacy = true;
    if (!consents.ai_processing) newErrors.ai_processing = true;
    if (Object.keys(newErrors).length > 0) {
      setConsentErrors(newErrors);
      setError(m.consent_required_error());
      return false;
    }
    setConsentErrors({});
    return true;
  }

  // SEC-006: Client-side password complexity validation
  const PASSWORD_REQUIREMENTS = [
    { test: (p: string) => p.length >= 8, label: "At least 8 characters" },
    { test: (p: string) => /[A-Z]/.test(p), label: "Uppercase letter" },
    { test: (p: string) => /[a-z]/.test(p), label: "Lowercase letter" },
    { test: (p: string) => /[0-9]/.test(p), label: "Number" },
    { test: (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(p), label: "Special character" },
  ];

  function isPasswordValid(pw: string): boolean {
    return PASSWORD_REQUIREMENTS.every((r) => r.test(pw));
  }

  function buildConsentPayload() {
    const locale = getLocale();
    return (Object.keys(consents) as Array<keyof ConsentState>)
      .filter((key) => consents[key] || key === "terms_privacy" || key === "ai_processing")
      .map((type) => ({
        type,
        granted: consents[type],
        version: CONSENT_VERSIONS[type],
        text: getConsentText(type, locale),
      }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!validateConsents()) {
      setLoading(false);
      return;
    }

    // SEC-006: Client-side password check before server call
    if (!isPasswordValid(password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      setLoading(false);
      return;
    }

    try {
      const result = await signUp.email({ name, email, password });
      if (result.error) {
        // SEC-011: Prevent user enumeration — always show generic message
        // Never reveal whether an email address is already registered
        setError(m.auth_error_signup_failed());
      } else {
        trackEvent("user_signed_up", {
          method: "email",
        });
        // ANAL-02: Fire signup_from_blog if user came from a blog page (sessionStorage breadcrumb)
        try {
          const blogRef = typeof window !== "undefined" ? sessionStorage.getItem("blog_referrer") : null;
          if (blogRef) {
            const { pageType: sourcePageType } = JSON.parse(blogRef) as { pageType?: unknown };
            const safePageType = asBlogPageType(sourcePageType);
            if (safePageType) trackEvent("signup_from_blog", { source_pageType: safePageType });
            sessionStorage.removeItem("blog_referrer");
          }
        } catch {
          // Malformed JSON or sessionStorage unavailable — skip attribution
        }
        // Affonso affiliate signup tracking
        if (typeof window !== "undefined" && (window as any).Affonso?.signup) {
          (window as any).Affonso.signup(email);
        }
        // Record consent — fire and forget (must not block navigation)
        client.consent.record({ consents: buildConsentPayload(), source: "signup" }).catch(() => {
          console.error("[consent] Failed to record consent after signup");
        });
        clearSessionCache();
        navigate({ to: "/onboarding" });
      }
    } catch {
      setError(m.auth_error_signup_generic());
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md space-y-6 animate-fade-up">
        {/* Brand anchor */}
        <Link to="/" className="flex justify-center no-underline" aria-label="Alchema home">
          <AlchemaLogo style={{ fontSize: "1.5rem" }} />
        </Link>

        <div className="card p-8 space-y-6" style={{ borderRadius: "var(--radius-xl, 1.25rem)" }}>
          <div className="text-center">
            <h1 className="font-semibold tracking-tight" style={{ fontSize: "var(--text-2xl, 1.5rem)", color: "var(--text)", letterSpacing: "var(--tracking-snug, -0.015em)" }}>
              {m.auth_signup_title()}
            </h1>
            <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>{m.auth_signup_subtitle()}</p>
          </div>

          {referralCode && (
            <div
              className="rounded-lg border px-4 py-3 text-sm"
              style={{
                borderColor: "var(--accent-subtle)",
                background: "var(--accent-subtle)",
                color: "var(--accent-text)",
              }}
              role="status"
            >
              {m.signup_referral_banner()}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="signup-form" className="space-y-4">
            {error && (
              <div id="signup-error" role="alert" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--error)", background: "var(--error-subtle)", color: "var(--error)" }}>
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="name" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                {m.auth_full_name_label()}
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus -- signup name field is the primary action on the page
                autoFocus
                autoComplete="name"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "signup-error" : undefined}
                className="input"
                placeholder={m.auth_full_name_placeholder()}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                {m.auth_email_label()}
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "signup-error" : undefined}
                className="input"
                placeholder={m.auth_email_placeholder()}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                {m.auth_password_label()}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? "signup-error" : "password-requirements"}
                  className="input"
                  style={{ paddingRight: "2.5rem" }}
                  placeholder={m.auth_password_placeholder_min()}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                  style={{ color: "var(--text-muted)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-muted)"; }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
              {/* SEC-006: Password requirements indicator */}
              <ul id="password-requirements" className="mt-2 space-y-0.5 text-xs" style={{ opacity: password.length > 0 ? 1 : 0.6 }}>
                {PASSWORD_REQUIREMENTS.map((req, i) => {
                  const met = req.test(password);
                  return (
                    <li key={i} className="flex items-center gap-1.5" style={{ color: met ? "var(--success)" : "var(--text-muted)" }}>
                      <span className="mono">{met ? "✓" : "○"}</span> {req.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <ConsentCheckboxes
              consents={consents}
              onChange={(updated) => {
                setConsents(updated);
                setConsentErrors({});
                if (error === m.consent_required_error()) setError("");
              }}
              errors={consentErrors}
              disabled={loading}
            />

            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              * = {m.consent_required_marker()}
            </p>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-accent w-full disabled:opacity-50"
            >
              {loading ? m.auth_signup_creating() : m.auth_signup_cta()}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: "var(--border-subtle)" }} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3" style={{ background: "var(--surface)", color: "var(--text-muted)" }}>
                {m.auth_or_continue_with()}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!validateConsents()) return;

              // Store consent state in sessionStorage before OAuth redirect
              try {
                sessionStorage.setItem("alchema_consent_state", JSON.stringify({
                  consents,
                  timestamp: Date.now(),
                }));
              } catch {
                // sessionStorage unavailable — proceed anyway
              }

              signIn.social({
                provider: "google",
                callbackURL: localizeInternalHref("/onboarding"),
              });
            }}
            className="btn btn-secondary w-full"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {m.auth_google()}
          </button>
        </div>

        <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
          {m.auth_has_account()}{" "}
          <Link to="/auth/login" className="font-medium hover:underline" style={{ color: "var(--text)" }}>
            {m.auth_sign_in()}
          </Link>
        </p>
      </div>
    </div>
  );
}
